import { animate, state, style, transition, trigger } from '@angular/animations';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { AbstractPlayButtonSpinnerService } from '../../services';
import { DestroyableBase } from '../../utils';
import { AbstractPreviewOverlayGtmService } from './abstract-preview-overlay-gtm.service';
import {
  ClosableOverlay,
  ClosableOverlayToken,
  PreviewOverlayViewModelToken,
  ShallowPreviewOverlayViewModel,
} from './preview-overlay.view-model';

@Component({
  selector: 'osn-shared-preview-overlay',
  templateUrl: './preview-overlay.component.html',
  styleUrls: ['./preview-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.9)' }),
        animate('175ms ease-in-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
      transition(':leave', [animate('175ms ease-in-out', style({ opacity: 0, transform: 'scale(0.9)' }))]),
    ]),
    trigger('expandAnimation', [
      state('close', style({ height: '32px' })),
      state('open', style({ height: '*' })),
      transition('open <=> close', animate('500ms ease-in-out')),
    ]),
  ],
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        touchendHideDelay: 0,
        position: 'above',
      } as MatTooltipDefaultOptions,
    },
  ],
})
export class PreviewOverlayComponent extends DestroyableBase implements OnInit, OnDestroy {
  public isDescriptionOpen = false;
  public readonly shouldShowPlayButtonSpinner$ = this.playButtonSpinnerService.showLoadingSpinner$;

  @HostListener('mouseleave')
  public mouseleave(): void {
    this.closable.setPreviewHoverState({ isMouseOver: false });
  }

  @HostListener('mouseenter')
  public mouseenter(): void {
    this.closable.setPreviewHoverState({ isMouseOver: true });
  }

  constructor(
    @Inject(PreviewOverlayViewModelToken) public readonly vm: ShallowPreviewOverlayViewModel,
    @Inject(ClosableOverlayToken) public readonly closable: ClosableOverlay,
    private readonly router: Router,
    private readonly previewOverlayGtmService: AbstractPreviewOverlayGtmService,
    private readonly overlayContainer: OverlayContainer,
    private readonly playButtonSpinnerService: AbstractPlayButtonSpinnerService
  ) {
    super();
    this.overlayContainer.getContainerElement().classList.add('preview-overlay-container');
  }

  public ngOnInit() {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter((val) => val instanceof NavigationEnd)
      )
      .subscribe(() => this.closable.setPreviewHoverState({ isMouseOver: false }));
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.overlayContainer.getContainerElement().classList.remove('preview-overlay-container');
    this.closable.onOverlayDestroyed();
  }

  // required for google analytics
  public get navigationState(): { category: string } {
    return { category: this.vm.type };
  }

  public toggleDescription(): void {
    this.isDescriptionOpen = !this.isDescriptionOpen;
    if (this.isDescriptionOpen) {
      this.previewOverlayGtmService.measureExpandHoverDescription(this.vm);
    }
  }

  public onGenreClick(genreName: string): void {
    this.previewOverlayGtmService.measureGenreClick(this.vm, genreName);
  }
}
